import type { OkBody } from '../Models/ApiModel';
import type {
  BulkIssueResultModel,
  BusinessSettingsModel,
  ComplianceStatusModel,
  OpenFormatRunModel,
  OpenFormatSummaryModel,
  ReceiptModel,
  ReceiptStatsModel,
  UnreportedSummaryModel
} from '../Models/ReceiptModel';
import { http } from './httpService';

export interface ReceiptQuery {
  status?: string;
  student?: string;
  from?: string;
  to?: string;
  q?: string;
}

function toQueryString(query: ReceiptQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) params.set(key, value);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const ReceiptService = {
  list: (query: ReceiptQuery = {}) => http.get<ReceiptModel[]>(`/receipts${toQueryString(query)}`),

  get: (id: number) => http.get<ReceiptModel>(`/receipts/${id}`),

  createDraft: (draft: Record<string, unknown>) => http.post<ReceiptModel>('/receipts', draft),

  updateDraft: (id: number, draft: Record<string, unknown>) =>
    http.put<ReceiptModel>(`/receipts/${id}`, draft),

  deleteDraft: (id: number) => http.delete<OkBody>(`/receipts/${id}`),

  /**
   * Issuing allocates the serial. The key makes a retry safe: if the first
   * attempt timed out after the server had already issued, sending the same
   * key returns that receipt instead of burning a second number.
   *
   * The date is the server's, not ours. A document is dated the day it was
   * produced, and letting a screen choose otherwise is backdating.
   */
  issue: (id: number) =>
    http.post<ReceiptModel>(`/receipts/${id}/issue`, { idempotencyKey: `issue-${id}` }),

  cancel: (id: number, reason: string) =>
    http.post<ReceiptModel>(`/receipts/${id}/cancel`, { reason }),

  /**
   * Recorded for the audit log, so a handed-over document leaves a trace. The
   * updated receipt comes back because the first print consumes the one
   * original and everything after it has to render itself as a copy.
   */
  recordEvent: (id: number, type: 'printed' | 'downloaded' | 'sent') =>
    http.post<ReceiptModel>(`/receipts/${id}/events`, { type }),

  unreported: () => http.get<UnreportedSummaryModel>('/receipts/unreported'),

  /** Merges a student's unreported lessons into one draft. */
  consolidate: (student: string) =>
    http.post<ReceiptModel>('/receipts/consolidate', { student }),

  /** One receipt per unreported lesson, across every student, issued immediately. */
  bulkIssue: () => http.post<BulkIssueResultModel>('/receipts/bulk-issue'),

  stats: () => http.get<ReceiptStatsModel>('/receipts/stats'),

  reports: () => http.get<Record<string, unknown>>('/receipts/reports'),

  getSettings: () => http.get<BusinessSettingsModel>('/receipt-settings'),

  saveSettings: (settings: Partial<BusinessSettingsModel>) =>
    http.put<BusinessSettingsModel>('/receipt-settings', settings),

  /** What is still missing before a document this business issues is legal. */
  compliance: () => http.get<ComplianceStatusModel>('/receipt-compliance'),

  /**
   * The uniform-structure files for a period, and what the server says about
   * them. §5.4 has the summary screen name the path the files were saved
   * under and count the records of each type; both describe this run, so they
   * are read off this response rather than asked for separately a moment
   * later, when the minute in the path may already have turned.
   */
  downloadOpenFormat: async (from: string, to: string): Promise<OpenFormatRunModel> => {
    const { blob, headers } = await http.download(
      `/receipts/openformat?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
    const counts = JSON.parse(headers.get('X-Openformat-Counts') ?? '{}') as Record<string, number>;
    return {
      blob,
      from,
      to,
      path: headers.get('X-Openformat-Path') ?? '',
      softwareName: decodeURIComponent(headers.get('X-Openformat-Software') ?? ''),
      records: Object.entries(counts).map(([code, count]) => ({ code, count }))
    };
  },

  /**
   * §2.6 of the uniform-structure instructions: how many documents of each
   * type in appendix 1 the period holds and for how much. Comes from the same
   * query the export runs, so the report and the file cannot disagree.
   */
  openFormatSummary: (from: string, to: string) => http.get<OpenFormatSummaryModel>(
    `/receipts/openformat-summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
  )
};
